import { useCallback, useEffect, useState } from 'react';
import { PageHeader, EmptyState, TableSkeleton } from '../components/common/PageHeader';
import { SearchFilterBar } from '../components/common/SearchFilterBar';
import { PaginationControls } from '../components/common/PaginationControls';
import { Modal } from '../components/common/Modal';
import { OrderStatusBadge } from '../components/common/StatusBadge';
import { listOrders, updateOrderStatus } from '../services/orderService';
import { getErrorMessage } from '../services/apiClient';
import { useToast } from '../context/ToastContext';
import { formatCurrency, formatDateTime } from '../utils/format';

export default function OrdersPage() {
  const { showToast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await listOrders(params);
      setOrders(res.data || []);
      setMeta(res.meta || { total: 0, totalPages: 1 });
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, showToast]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleStatusChange = async (orderId, status) => {
    setStatusUpdating(true);
    try {
      const res = await updateOrderStatus(orderId, status);
      showToast('Order status updated');
      setSelectedOrder(res.data || { ...selectedOrder, status });
      fetchOrders();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setStatusUpdating(false);
    }
  };

  const filtered = orders.filter((order) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const userName = order.user?.name?.toLowerCase() || '';
    const userEmail = order.user?.email?.toLowerCase() || '';
    const orderId = order._id.toLowerCase();
    return userName.includes(q) || userEmail.includes(q) || orderId.includes(q);
  });

  return (
    <div>
      <PageHeader title="Orders" description="View and track customer orders" />

      <div className="card">
        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by customer or order ID..."
          filters={[{
            key: 'status',
            label: 'Status',
            options: [
              { value: 'pending', label: 'Pending' },
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'shipped', label: 'Shipped' },
              { value: 'out_for_delivery', label: 'Out for Delivery' },
              { value: 'delivered', label: 'Delivered' },
              { value: 'cancelled', label: 'Cancelled' },
            ],
          }]}
          filterValues={{ status: statusFilter }}
          onFilterChange={(_, value) => { setStatusFilter(value); setPage(1); }}
          onClear={() => { setSearch(''); setStatusFilter('all'); setPage(1); }}
        />

        {loading ? (
          <TableSkeleton cols={6} />
        ) : filtered.length === 0 ? (
          <EmptyState title="No orders found" description="Orders will appear here when customers checkout." />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((order) => (
                    <tr key={order._id}>
                      <td className="cell-mono">{order._id.slice(-8).toUpperCase()}</td>
                      <td>
                        <p className="cell-title">{order.user?.name || '—'}</p>
                        <p className="cell-sub">{order.user?.email}</p>
                      </td>
                      <td>{order.items?.length || 0}</td>
                      <td>{formatCurrency(order.total)}</td>
                      <td><OrderStatusBadge status={order.status} /></td>
                      <td>{formatDateTime(order.createdAt)}</td>
                      <td>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelectedOrder(order)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={page} totalPages={meta.totalPages} totalItems={meta.total} onPageChange={setPage} />
          </>
        )}
      </div>

      <Modal
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        title={`Order #${selectedOrder?._id?.slice(-8).toUpperCase()}`}
        size="lg"
      >
        {selectedOrder && (
          <div className="order-detail">
            <div className="detail-grid">
              <div>
                <p className="detail-label">Customer</p>
                <p>{selectedOrder.user?.name}</p>
                <p className="cell-sub">{selectedOrder.user?.email}</p>
              </div>
              <div>
                <p className="detail-label">Update Status</p>
                <p className="cell-sub" style={{ marginBottom: 8 }}>
                  Change status as the order moves from confirmed to shipped, out for delivery, and delivered.
                </p>
                <div className="form-group" style={{ marginTop: 8 }}>
                  <select
                    className="input"
                    value={selectedOrder.status || 'pending'}
                    disabled={statusUpdating}
                    onChange={(e) => handleStatusChange(selectedOrder._id, e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="shipped">Shipped</option>
                    <option value="out_for_delivery">Out for Delivery</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              <div>
                <p className="detail-label">Total</p>
                <p className="detail-value">{formatCurrency(selectedOrder.total)}</p>
              </div>
              <div>
                <p className="detail-label">Date</p>
                <p>{formatDateTime(selectedOrder.createdAt)}</p>
              </div>
            </div>
            <div className="detail-section">
              <p className="detail-label">Shipping Address</p>
              <p>{selectedOrder.shippingAddress?.name}</p>
              <p>{selectedOrder.shippingAddress?.line1}</p>
              <p>{selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.state} {selectedOrder.shippingAddress?.pincode}</p>
              <p>{selectedOrder.shippingAddress?.phone}</p>
            </div>
            <div className="detail-section">
              <p className="detail-label">Items</p>
              <table className="data-table compact">
                <thead>
                  <tr><th>Product</th><th>Qty</th><th>Price</th></tr>
                </thead>
                <tbody>
                  {selectedOrder.items?.map((item, i) => (
                    <tr key={i}>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>{formatCurrency(item.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
